/* @flow */

import storage from './storage';
import {
  getCookie,
  setCookie,
  deleteCookie,
  isCookieSupported
} from './cookie';
import { getTopLevelDomain } from '../utils';
import { isSecure } from './browserutil';

const STORAGE_COOKIE = 'cookie';
const STORAGE_HTML5 = 'html5';
export const DEFAULT_STORAGE = [STORAGE_HTML5];

const SUFFIX_EXPIRY = 'exp';
const SUFFIX_LAST = 'last';

const SUPPORTED_STORAGES = new Set([STORAGE_COOKIE, STORAGE_HTML5]);

function filterSupported(storageTypes: Array<string> = DEFAULT_STORAGE) {
  return storageTypes
    .filter(v => SUPPORTED_STORAGES.has(v))
    .filter(v => {
      if (v === STORAGE_COOKIE) {
        return isCookieSupported();
      }
      if (v === STORAGE_HTML5) {
        return storage.isSupported();
      }
      return false;
    });
}

export function isStorageSupported(
  storageTypes: Array<string> = DEFAULT_STORAGE
): boolean {
  return filterSupported(storageTypes).length > 0;
}

// get the value from any one of the storage used
export function getFromStore(
  name: string,
  storageTypes: Array<string> = DEFAULT_STORAGE
): string {
  let val = '';
  filterSupported(storageTypes).some(st => {
    if (st === STORAGE_COOKIE) {
      val = getCookie(name);
      return val.length > 0;
    }
    if (st === STORAGE_HTML5) {
      // get both named value & expiry
      const expiry = storage.get(`${name}_${SUFFIX_EXPIRY}`);
      const value = decodeURIComponent(storage.get(name));
      // check for expiry
      val =
        expiry.length > 0 && new Date(expiry).getTime() - Date.now() <= 0
          ? ''
          : value;

      return val.length > 0;
    }
    return false;
  });
  return val;
}

export function getFromAllStore(name: string): string {
  return getFromStore(name, [...SUPPORTED_STORAGES]);
}

export function removeFromStore(
  name: string = '',
  storageTypes: Array<string> = DEFAULT_STORAGE
) {
  // prepare all the ids
  if (name.length === 0) {
    return;
  }
  const arrNames = [name, `${name}_${SUFFIX_EXPIRY}`, `${name}_${SUFFIX_LAST}`];
  filterSupported(storageTypes).forEach(st => {
    if (st === STORAGE_COOKIE) {
      arrNames.forEach(n => deleteCookie(n));
    }
    if (st === STORAGE_HTML5) {
      arrNames.forEach(n => storage.remove(n));
    }
  });
}

// save to all the configured storage locations
export function saveToStore(
  o: cookieData,
  peridicRefresh: boolean = false,
  storageTypes: Array<string> = DEFAULT_STORAGE
): void {
  filterSupported(storageTypes).forEach(st => {
    if (st === STORAGE_COOKIE) {
      const {
        name,
        value,
        doc = window.Document,
        expires = 60 * 60 * 24 * 365,
        domain = getTopLevelDomain(),
        path = '/',
        secure = isSecure()
      } = o;
      setCookie(name, value, doc, expires, domain, path, secure);
      if (peridicRefresh) {
        setCookie(
          `${name}_${SUFFIX_LAST}`,
          new Date().toUTCString(),
          doc,
          expires,
          domain,
          path,
          secure
        );
      }
      return;
    }
    if (st === STORAGE_HTML5) {
      const { name = '', value, expires = -1 } = o;
      if (name.length === 0 || !value) {
        // nothing to store here.
        return;
      }
      if (expires > 0) {
        const expiresMs = expires * 1000;
        const expiresStr = new Date(Date.now() + expiresMs).toUTCString();
        storage.store(name, encodeURIComponent(value));
        storage.store(`${name}_${SUFFIX_EXPIRY}`, expiresStr);
        if (peridicRefresh) {
          storage.store(`${name}_${SUFFIX_LAST}`, new Date().toUTCString());
        }
      }
    }
  });
}

export function getStoredValueTS(
  name: string = '',
  refreshSeconds: number = 0,
  storageTypes: Array<string> = DEFAULT_STORAGE
): number {
  const defaultRet = 0;
  if (name.length === 0) {
    return defaultRet;
  }
  let value = '';
  let lastUpdateTime = '';
  let expiresTime = '';
  const lastName = `${name}_${SUFFIX_LAST}`;
  const expireName = `${name}_${SUFFIX_EXPIRY}`;

  filterSupported(storageTypes).some(st => {
    if (st === STORAGE_COOKIE) {
      value = getCookie(name);
      lastUpdateTime = getCookie(lastName);
    }
    if (st === STORAGE_HTML5) {
      value = storage.get(name);
      lastUpdateTime = storage.get(lastName);
      expiresTime = storage.get(expireName);
    }
    // only when the value is there, last stored timestamp is valid
    return value.length > 0;
  });

  // if value is missing, we need to refresh for sure.
  if (value.length === 0) {
    return defaultRet;
  }

  // if we have expires string, it's from HTML5. Cookie expires automatically anyway.
  // perform expires check now.
  if (
    expiresTime.length > 0 &&
    new Date(expiresTime).getTime() - Date.now() <= 0
  ) {
    return defaultRet;
  }

  // if no periodic refresh, we have a valid value in the cookie as per above checks
  if (refreshSeconds <= 0) {
    return lastUpdateTime.length > 0
      ? new Date(lastUpdateTime).getTime()
      : Date.now();
  }

  // if last is missing, let's refresh for sure
  if (lastUpdateTime.length === 0) {
    return defaultRet;
  }

  const storedDate = new Date(lastUpdateTime);
  const isExpired =
    storedDate && Date.now() - storedDate.getTime() > refreshSeconds * 1000;
  return isExpired ? defaultRet : storedDate.getTime();
}
